import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { waLink, waReason } from "@/lib/wa";
import { Link } from "react-router-dom";
import {
    WhatsappLogo, Warning, ChatCircleDots, ClockUser, Spinner, X,
} from "@phosphor-icons/react";

const REASON_THEMES = {
    OPEN_ISSUE:     { label: "Open issue",       Icon: Warning,        bg: "bg-rose-100 text-rose-700" },
    LOW_COMPLIANCE: { label: "Low compliance",   Icon: ClockUser,      bg: "bg-amber-100 text-amber-800" },
    NO_RECENT_LOG:  { label: "Hasn't logged",    Icon: ChatCircleDots, bg: "bg-violet-100 text-violet-800" },
};

const RISK_PILL = {
    high:   "bg-rose-600 text-white",
    medium: "bg-amber-500 text-white",
    low:    "bg-zinc-300 text-zinc-700",
};

function waLinkLocal(phone, msg) {
    return waLink(phone, msg);
}

export default function PatientNudges() {
    const [rows, setRows] = useState(null);
    const [error, setError] = useState(null);
    const [showAll, setShowAll] = useState(false);
    const [dismissed, setDismissed] = useState(() => {
        try {
            const stored = window.localStorage.getItem("doctor.dismissedNudges");
            return new Set(stored ? JSON.parse(stored) : []);
        } catch { return new Set(); }
    });

    useEffect(() => {
        api.get("/doctor/at-risk-patients")
            .then((r) => setRows(r.data || []))
            .catch((e) => setError(e.response?.data?.detail || "Couldn't load"));
    }, []);

    const persistDismissed = (next) => {
        try {
            window.localStorage.setItem("doctor.dismissedNudges", JSON.stringify(Array.from(next)));
        } catch { /* localStorage can be unavailable in private mode */ }
        setDismissed(next);
    };

    const visible = useMemo(() => {
        if (!rows) return [];
        return rows.filter((r) => !dismissed.has(r.case_id));
    }, [rows, dismissed]);

    const dismiss = (caseId) => {
        const next = new Set(dismissed);
        next.add(caseId);
        persistDismissed(next);
    };

    const undoDismissAll = () => {
        try { window.localStorage.removeItem("doctor.dismissedNudges"); } catch {/**/}
        setDismissed(new Set());
    };

    if (rows === null && !error) {
        return (
            <div className="bg-card border border-border rounded-2xl p-6 text-center text-sm text-muted-foreground">
                <Spinner size={16} className="inline-block animate-spin mr-2" /> Looking for patients who need a check-in…
            </div>
        );
    }
    if (error) {
        return (
            <div className="bg-card border border-rose-200 rounded-2xl p-4 text-xs text-rose-700">
                Couldn't load nudges: {error}
            </div>
        );
    }
    if (rows.length === 0) {
        return (
            <div className="bg-card border border-emerald-200 bg-emerald-50/40 rounded-2xl p-5 flex items-center gap-3" data-testid="nudges-empty">
                <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center">
                    <ChatCircleDots size={18} weight="fill" />
                </div>
                <div>
                    <div className="font-display font-bold">Nothing flagged today</div>
                    <div className="text-xs text-muted-foreground">Every active patient is on track. Nice.</div>
                </div>
            </div>
        );
    }
    if (visible.length === 0) {
        return (
            <div className="bg-card border border-border rounded-2xl p-5 flex items-center justify-between gap-3" data-testid="nudges-all-dismissed">
                <div className="text-xs text-muted-foreground">All {rows.length} nudge{rows.length === 1 ? "" : "s"} dismissed for today.</div>
                <button onClick={undoDismissAll} className="text-xs font-semibold text-primary hover:underline" data-testid="nudges-undo-all">
                    Show again
                </button>
            </div>
        );
    }

    const displayed = showAll ? visible : visible.slice(0, 4);

    return (
        // Inner header removed — the wrapping Dashboard <section> already
        // titles this block "Compliance — needs attention" with a subtitle
        // and "View full insights" link. Showing another title here was a
        // duplicate. The row count + jump-to-cases link have been folded
        // into the small toolbar above the list.
        <div className="bg-card border border-border rounded-2xl p-5 sm:p-6" data-testid="nudges-section">
            <div className="flex items-center justify-between mb-3">
                <div className="text-xs text-muted-foreground">
                    <span className="font-semibold text-foreground tabular-nums">{visible.length}</span> patient{visible.length === 1 ? "" : "s"} could use a nudge today
                </div>
            </div>

            <div className="space-y-3">
                {displayed.map((r) => (
                    <NudgeRow key={r.case_id} row={r} onDismiss={() => dismiss(r.case_id)} />
                ))}
            </div>

            {visible.length > 4 && (
                <button
                    onClick={() => setShowAll((v) => !v)}
                    className="mt-4 text-xs font-semibold text-primary hover:underline w-full text-center"
                    data-testid="nudges-show-more"
                >
                    {showAll ? "Show fewer" : `Show ${visible.length - 4} more`}
                </button>
            )}
        </div>
    );
}

function NudgeRow({ row, onDismiss }) {
    const theme = REASON_THEMES[row.reason] || { label: row.reason, Icon: Warning, bg: "bg-zinc-100 text-zinc-700" };
    const wa = waLinkLocal(row.patient_phone, row.whatsapp_template);
    const waBlockReason = waReason(row.patient_phone);
    const Icon = theme.Icon;

    let metric = "—";
    if (row.reason === "LOW_COMPLIANCE") {
        metric = `${row.avg_hours_7d}h avg`;
    } else if (row.reason === "NO_RECENT_LOG") {
        metric = row.days_since_last_log ? `${row.days_since_last_log}d quiet` : "No logs";
    } else if (row.reason === "OPEN_ISSUE") {
        metric = `${row.open_issues_count} open`;
    }

    return (
        <div
            data-testid={`nudge-${row.case_id}`}
            className="flex items-center gap-3 sm:gap-4 p-3 rounded-xl border border-border bg-secondary/20 hover:bg-secondary/40 transition"
        >
            <div className={`shrink-0 w-9 h-9 rounded-full grid place-items-center ${theme.bg}`}>
                <Icon size={15} weight="bold" />
            </div>

            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                    <Link
                        to={`/doctor/patients/${row.patient_id}`}
                        className="text-sm font-semibold truncate hover:underline"
                        data-testid={`nudge-patient-link-${row.case_id}`}
                    >
                        {row.patient_name || "Patient"}
                    </Link>
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${theme.bg}`}>
                        {theme.label}
                    </span>
                    <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${RISK_PILL[row.risk]}`}>
                        {row.risk}
                    </span>
                </div>
                <div className="text-[11px] text-muted-foreground mt-0.5">
                    <span className="font-mono text-primary">{row.case_code}</span>
                    <span> · </span>
                    <span className="tabular-nums">{metric}</span>
                    {row.patient_phone && <> <span> · </span><span>{row.patient_phone}</span></>}
                </div>
            </div>

            <div className="flex items-center gap-1.5 shrink-0">
                {wa ? (
                    <a
                        href={wa} target="_blank" rel="noopener noreferrer"
                        onClick={() => {
                            // Log this nudge so it appears on the patient
                            // profile history. Fire-and-forget so WhatsApp
                            // opens immediately.
                            api.post("/doctor/nudges", {
                                patient_id: row.patient_id,
                                case_id: row.case_id,
                                reason: row.reason || "manual_nudge",
                                message: row.whatsapp_template || "",
                                channel: "whatsapp",
                            }).catch(() => { /* non-critical */ });
                        }}
                        className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-3 h-9 transition"
                        data-testid={`nudge-whatsapp-${row.case_id}`}
                    >
                        <WhatsappLogo size={14} weight="fill" /> WhatsApp
                    </a>
                ) : (
                    <span
                        className="text-[10px] text-muted-foreground italic"
                        title={waBlockReason || "Phone unavailable"}
                        data-testid={`nudge-no-phone-${row.case_id}`}
                    >
                        {waBlockReason || "No phone on file"}
                    </span>
                )}
                <button
                    type="button" onClick={onDismiss}
                    className="w-9 h-9 grid place-items-center rounded-full text-muted-foreground hover:bg-foreground/5"
                    aria-label="Dismiss"
                    data-testid={`nudge-dismiss-${row.case_id}`}
                >
                    <X size={14} weight="bold" />
                </button>
            </div>
        </div>
    );
}
